import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { user as userSchema } from '../lib/schema.js';
import { selectCurrentOrganizationId } from './auth.js';
import { selectEntities } from './entities.js';

// API Actions

const RETRIEVE_USERS_REQUEST = 'changesync/users/RETRIEVE_USERS_REQUEST';
const RETRIEVE_USERS_SUCCESS = 'changesync/users/RETRIEVE_USERS_SUCCESS';
const RETRIEVE_USERS_FAILURE = 'changesync/users/RETRIEVE_USERS_FAILURE';
const RETRIEVE_USER_REQUEST = 'changesync/users/RETRIEVE_USER_REQUEST';
const RETRIEVE_USER_SUCCESS = 'changesync/users/RETRIEVE_USER_SUCCESS';
const RETRIEVE_USER_FAILURE = 'changesync/users/RETRIEVE_USER_FAILURE';
const CREATE_USER_REQUEST = 'changesync/users/CREATE_USER_REQUEST';
const CREATE_USER_SUCCESS = 'changesync/users/CREATE_USER_SUCCESS';
const CREATE_USER_FAILURE = 'changesync/users/CREATE_USER_FAILURE';
const UPDATE_USER_REQUEST = 'changesync/users/UPDATE_USER_REQUEST';
const UPDATE_USER_SUCCESS = 'changesync/users/UPDATE_USER_SUCCESS';
const UPDATE_USER_FAILURE = 'changesync/users/UPDATE_USER_FAILURE';
const DELETE_USER_REQUEST = 'changesync/users/DELETE_USER_REQUEST';
const DELETE_USER_SUCCESS = 'changesync/users/DELETE_USER_SUCCESS';
const DELETE_USER_FAILURE = 'changesync/users/DELETE_USER_FAILURE';
const SEARCH_USERS_REQUEST = 'changesync/users/SEARCH_USERS_REQUEST';
const SEARCH_USERS_SUCCESS = 'changesync/users/SEARCH_USERS_SUCCESS';
const SEARCH_USERS_FAILURE = 'changesync/users/SEARCH_USERS_FAILURE';

// Search Actions
const UPDATE_SEARCH_QUERY = 'changesync/users/UPDATE_SEARCH_QUERY';
const SEARCH_USERS = 'changesync/users/SEARCH_USERS';

const initialState = {
  retrieveUsers: {
    usersIds: [],
    pending: false,
    success: false,
    error: null,
  },

  searchUsers: {
    usersIds: [],
    query: '',
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_USERS_REQUEST:
      return {
        ...state,
        retrieveUsers: {
          ...state.retrieveUsers,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_USERS_SUCCESS:
      return {
        ...state,
        retrieveUsers: {
          usersIds: action.payload.result,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_USERS_FAILURE:
      return {
        ...state,
        retrieveUsers: {
          ...state.retrieveUsers,
          pending: false,
          error: action.error,
        },
      };

    case SEARCH_USERS_REQUEST:
      return {
        ...state,
        searchUsers: {
          ...state.searchUsers,
          pending: true,
          success: false,
          error: null,
        },
      };

    case SEARCH_USERS_SUCCESS:
      return {
        ...state,
        searchUsers: {
          ...state.searchUsers,
          usersIds: action.payload.result,
          query: action.meta.query,
          pending: false,
          success: true,
        },
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    default:
      return state;
  }
};

export const retrieveUsers = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_USERS_REQUEST,
        RETRIEVE_USERS_SUCCESS,
        RETRIEVE_USERS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/users`,
      method: HTTP_METHODS.GET,
      schema: [userSchema],
      query,
    },
  });
};

export const retrieveUser = (id) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_USER_REQUEST,
        RETRIEVE_USER_SUCCESS,
        RETRIEVE_USER_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/users/${id}`,
      method: HTTP_METHODS.GET,
      schema: userSchema,
    },
  });
};

export const createUser = (user) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [CREATE_USER_REQUEST, CREATE_USER_SUCCESS, CREATE_USER_FAILURE],
      endpoint: `organizations/${organizationId}/users`,
      method: HTTP_METHODS.POST,
      schema: userSchema,
      body: { user },
    },
  });
};

export const updateUser = (userId, user) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [UPDATE_USER_REQUEST, UPDATE_USER_SUCCESS, UPDATE_USER_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}`,
      method: HTTP_METHODS.PUT,
      schema: userSchema,
      body: { user },
    },
  });
};

export const deleteUser = (userId) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  return dispatch({
    [CALL_API]: {
      types: [DELETE_USER_REQUEST, DELETE_USER_SUCCESS, DELETE_USER_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}`,
      method: HTTP_METHODS.DELETE,
    },
  });
};

export const searchUsers = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  const searchParam = query ? `?search=&search=${query}` : '';

  return dispatch({
    [CALL_API]: {
      types: [SEARCH_USERS_REQUEST, SEARCH_USERS_SUCCESS, SEARCH_USERS_FAILURE],
      endpoint: `organizations/${organizationId}/users/search${searchParam}`,
      method: HTTP_METHODS.GET,
      schema: [userSchema],
    },
    meta: {
      debounce: {
        time: 400,
        key: SEARCH_USERS,
      },
    },
  });
};

export const updateSearchQuery = (query) => ({
  type: UPDATE_SEARCH_QUERY,
  payload: query,
});

// Selectors

export const selectUser = (id) => (state) =>
  denormalize(id, userSchema, state.entities);

export const selectSearchUsersState = (state) => state.users.searchUsers;
export const selectRetrieveUsersState = (state) => state.users.retrieveUsers;

const selectSearchUsersIds = createSelector(
  selectSearchUsersState,
  (state) => state.usersIds
);

const selectRetrieveUsersIds = createSelector(
  selectRetrieveUsersState,
  (state) => state.usersIds
);

export const selectUsers = createSelector(
  selectRetrieveUsersIds,
  selectEntities,
  (usersIds, entities) =>
    denormalize(usersIds, [userSchema], entities).filter((u) => !u.archived)
);
export const selectUsersWithArchived = createSelector(
  selectRetrieveUsersIds,
  selectEntities,
  (usersIds, entities) =>
    denormalize(usersIds, [userSchema], entities).filter((u) => !u.archived)
);

export const selectSearchUsersResults = createSelector(
  selectSearchUsersIds,
  selectEntities,
  (usersIds, entities) => denormalize(usersIds, [userSchema], entities)
);
